/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type AskBoConfig = {
  scriptId: string;
  scriptUrl: string;
};

export type WebConfig = {
  askBoConfig?: AskBoConfig;
};

type WebConfigContextType = {
  config: WebConfig;
  updateConfig: (updates: Partial<WebConfig>) => void;
};

export const WebConfigContext = createContext<WebConfigContextType | undefined>(
  undefined,
);

type WebConfigProviderProps = WebConfig & {
  children: ReactNode;
};

export const WebConfigProvider: React.FC<WebConfigProviderProps> = ({
  children,
  ...initialConfig
}) => {
  const [config, setConfig] = useState<WebConfig>(initialConfig);
  const updateConfig = useCallback((updates: Partial<WebConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  }, []);

  const value = useMemo(
    () => ({
      config,
      updateConfig,
    }),
    [config, updateConfig],
  );

  return (
    <WebConfigContext.Provider value={value}>
      {children}
    </WebConfigContext.Provider>
  );
};

export const useWebConfig = (): WebConfigContextType => {
  const ctx = useContext(WebConfigContext);
  if (!ctx)
    throw new Error('useWebConfig must be used inside WebConfigProvider');
  return ctx;
};
